import { defineStore } from 'pinia'
import { computed, ref, watch, type WatchStopHandle } from 'vue'
import { debounce } from 'throttle-debounce'
import { collection, doc, onSnapshot, query, where, type QuerySnapshot } from 'firebase/firestore'
import { firestore } from '@/firebase'
import { platformCoordApi } from '@/components/cloudFunctions/platformCoord'
import type { PlatformCoordRouteTypeMap } from 'platform-coord-functions/client'
import {
  resolveConsultationHubFirestoreWatches,
  watchKeyForConsultationHubFirestoreWatches,
} from '@/components/consultation/hub/consultationHubFirestoreWatches'
import {
  consultationIdentityChanged,
  syncLastSeenFromSnapshot,
  type ConsultationIdentityMap,
} from '@/components/consultation/hub/consultationHubSnapshotSignal'
import {
  isHubEndedAwaitingResolve,
  pickFutureUpcomingSummary,
  pickInProgressAppointmentSummaries,
} from '@/components/consultation/hub/resolveConsultationHubWatchId'
import {
  CONSULTATIONS_HUB_FETCH_MAX_ATTEMPTS,
  CONSULTATIONS_HUB_FETCH_RETRY_DELAYS_MS,
  delayMs,
  formatConsultationsHubFetchError,
  isTransientConsultationsHubFetchError,
  logConsultationsHubFetchFailure,
} from '@/components/consultation/hub/consultationsHubFetch'
import { useCustomerStore } from '@/stores/customer'
import createLogger from '@/utils/logger'

const logger = createLogger('stores/customerConsultationsHub.ts')

// See ADR-DOM-005: hub list reload is snapshot-driven inside this store only.

export type CustomerConsultationSummary =
  PlatformCoordRouteTypeMap['listCustomerConsultationDetails']['response']['summaries'][number]

export type ClinicalFollowupBooking =
  PlatformCoordRouteTypeMap['listCustomerConsultationDetails']['response']['followupBooking']

export type AdminBookingInProgress =
  PlatformCoordRouteTypeMap['listCustomerConsultationDetails']['response']['adminBookingInProgress']

const DEFAULT_FOLLOWUP_BOOKING: ClinicalFollowupBooking = {
  eligible: false,
  preferredConsultantId: null,
  preferredTellescopeUserId: null,
  unlockingConsultationId: null,
  insurancePolicyLocked: false,
}

// Q12 developer decision (awaiting product confirm): payment_due is Past, not featured,
// so Upcoming is empty when Book starts a new visit.
const FEATURED_UI_STATUSES = ['upcoming', 'resume'] as const
const PAST_UI_STATUSES = [
  'pending',
  'completed',
  'no_show',
  'booking_cancelled',
  'cancelled',
  'payment_due',
] as const
const REFETCH_DEBOUNCE_MS = 300
const HUB_NOW_TICK_MS = 30_000

export function isResumeHoldExpired(
  summary: CustomerConsultationSummary,
  now = new Date(),
): boolean {
  if (summary.uiStatus !== 'resume' || !summary.checkoutExpiresAt) {
    return false
  }
  return new Date(summary.checkoutExpiresAt).getTime() <= now.getTime()
}

export function remainingResumeHoldMinutes(
  summary: CustomerConsultationSummary,
  now = new Date(),
): number | null {
  if (!summary.checkoutExpiresAt) {
    return null
  }
  const remainingMs = new Date(summary.checkoutExpiresAt).getTime() - now.getTime()
  if (remainingMs <= 0) {
    return 0
  }
  return Math.ceil(remainingMs / (60 * 1000))
}

export const useCustomerConsultationsHubStore = defineStore('customerConsultationsHub', () => {
  const customerStore = useCustomerStore()
  const summaries = ref<CustomerConsultationSummary[]>([])
  const followupBooking = ref<ClinicalFollowupBooking>(DEFAULT_FOLLOWUP_BOOKING)
  const adminBookingInProgress = ref<AdminBookingInProgress>(null)
  const hasLoaded = ref(false)
  const isLoading = ref(false)
  const isRefreshing = ref(false)
  const isUpcomingRefreshing = ref(false)
  const error = ref<string | null>(null)

  let unsubscribeConsultationsQuery: (() => void) | null = null
  let unsubscribeStripeCheckoutDocs: Array<() => void> = []
  let stopStripeCheckoutWatch: WatchStopHandle | null = null
  let stopActiveIdWatch: WatchStopHandle | null = null
  let debouncedReloadList: ReturnType<typeof debounce<() => void>> | null = null
  let consultationLastSeen: ConsultationIdentityMap = new Map()
  let reloadDirty = false
  let reloadGeneration = 0
  let activeReloadPromise: Promise<unknown> | null = null

  const nowMs = ref(Date.now())
  let hubNowInterval: ReturnType<typeof setInterval> | null = null

  const hubNow = computed(() => new Date(nowMs.value))

  function startHubNowTicker(): void {
    if (import.meta.env.MODE === 'test') {
      return
    }
    if (hubNowInterval) {
      return
    }
    hubNowInterval = setInterval(() => {
      nowMs.value = Date.now()
    }, HUB_NOW_TICK_MS)
  }

  function stopHubNowTicker(): void {
    if (!hubNowInterval) {
      return
    }
    clearInterval(hubNowInterval)
    hubNowInterval = null
  }

  function registerActiveReload(reloadPromise: Promise<void>): void {
    activeReloadPromise = reloadPromise
    void reloadPromise
      .finally(() => {
        if (activeReloadPromise === reloadPromise) {
          activeReloadPromise = null
        }
      })
      .catch(() => undefined)
  }

  function trackActiveReload(reloadPromise: Promise<void>): void {
    void reloadPromise.catch(() => undefined)
    registerActiveReload(reloadPromise)
  }

  const clientVisibleSummaries = computed(() =>
    summaries.value.filter((row) => !isResumeHoldExpired(row)),
  )

  const featuredSummary = computed(() => {
    const activeClinicalConsultationId =
      customerStore.customer?.activeClinicalConsultationId ?? null
    const now = hubNow.value

    for (const status of FEATURED_UI_STATUSES) {
      if (status === 'upcoming') {
        const match = pickFutureUpcomingSummary(
          clientVisibleSummaries.value,
          activeClinicalConsultationId,
          now,
        )
        if (match) {
          return match
        }
        continue
      }

      const match = clientVisibleSummaries.value.find((row) => row.uiStatus === status)
      if (match) {
        return match
      }
    }
    return null
  })

  /** @deprecated Use `featuredSummary` — kept for incremental migration. */
  const upcomingSummary = featuredSummary

  const inProgressSummaries = computed(() =>
    pickInProgressAppointmentSummaries(
      clientVisibleSummaries.value,
      featuredSummary.value?.consultationId,
      hubNow.value,
    ),
  )

  const pastSummaries = computed(() => {
    const featuredId = featuredSummary.value?.consultationId
    const inProgressIds = new Set(inProgressSummaries.value.map((row) => row.consultationId))
    const now = hubNow.value
    const endedUpcoming = clientVisibleSummaries.value
      .filter(
        (row) =>
          row.consultationId !== featuredId &&
          !inProgressIds.has(row.consultationId) &&
          isHubEndedAwaitingResolve(row, now),
      )
      .map((row) => ({ ...row, uiStatus: 'pending' as const }))
    const lifecyclePast = clientVisibleSummaries.value.filter((row) => {
      if (row.consultationId === featuredId || inProgressIds.has(row.consultationId)) {
        return false
      }
      if (isHubEndedAwaitingResolve(row, now)) {
        return false
      }
      return (PAST_UI_STATUSES as readonly string[]).includes(row.uiStatus)
    })
    return [...endedUpcoming, ...lifecyclePast]
  })

  const hasPastAppointments = computed(() => pastSummaries.value.length > 0)

  const hasAnyConsultations = computed(() => clientVisibleSummaries.value.length > 0)

  function clearState(): void {
    reloadGeneration += 1
    consultationLastSeen = new Map()
    reloadDirty = false
    summaries.value = []
    followupBooking.value = DEFAULT_FOLLOWUP_BOOKING
    adminBookingInProgress.value = null
    hasLoaded.value = false
    error.value = null
    isLoading.value = false
    isRefreshing.value = false
    isUpcomingRefreshing.value = false
    activeReloadPromise = null
  }

  async function runCoalescedReload(): Promise<void> {
    while (true) {
      await reloadList()
      if (!reloadDirty) {
        break
      }
      reloadDirty = false
    }
  }

  function signalReload(args: { immediate: boolean }): void {
    if (args.immediate) {
      debouncedReloadList?.cancel()
      if (activeReloadPromise) {
        reloadDirty = true
        return
      }
      trackActiveReload(runCoalescedReload())
      return
    }
    debouncedReloadList?.()
  }

  function onConsultationsQuerySnapshot(snap: QuerySnapshot): void {
    if (!hasLoaded.value) {
      syncLastSeenFromSnapshot(snap, consultationLastSeen)
      signalReload({ immediate: true })
      return
    }

    const immediate = consultationIdentityChanged(snap, consultationLastSeen)
    syncLastSeenFromSnapshot(snap, consultationLastSeen)
    signalReload({ immediate })
  }

  function teardownListeners(): void {
    stopHubNowTicker()
    unsubscribeConsultationsQuery?.()
    unsubscribeConsultationsQuery = null
    for (const unsubscribe of unsubscribeStripeCheckoutDocs) {
      unsubscribe()
    }
    unsubscribeStripeCheckoutDocs = []
    stopStripeCheckoutWatch?.()
    stopStripeCheckoutWatch = null
    stopActiveIdWatch?.()
    stopActiveIdWatch = null
    debouncedReloadList?.cancel()
    debouncedReloadList = null
  }

  function teardownStripeCheckoutListeners(): void {
    for (const unsubscribe of unsubscribeStripeCheckoutDocs) {
      unsubscribe()
    }
    unsubscribeStripeCheckoutDocs = []
  }

  function attachStripeCheckoutListeners(): void {
    teardownStripeCheckoutListeners()

    const watches = resolveConsultationHubFirestoreWatches({
      summaries: summaries.value,
      activeClinicalConsultationId: customerStore.customer?.activeClinicalConsultationId ?? null,
    })
    if (watches.length === 0 || !debouncedReloadList) {
      return
    }

    for (const watchTarget of watches) {
      let isFirstSnapshot = true
      const unsubscribe = onSnapshot(
        doc(
          firestore,
          watchTarget.collection,
          watchTarget.consultationId,
          'projections',
          watchTarget.projectionKey,
        ),
        () => {
          if (isFirstSnapshot) {
            isFirstSnapshot = false
            return
          }
          signalReload({ immediate: false })
        },
        () => undefined,
      )
      unsubscribeStripeCheckoutDocs.push(unsubscribe)
    }
  }

  function setupForCustomer(customerId: string): void {
    teardownListeners()
    clearState()
    isLoading.value = true
    startHubNowTicker()

    debouncedReloadList = debounce(REFETCH_DEBOUNCE_MS, () => {
      if (activeReloadPromise) {
        reloadDirty = true
        return
      }
      trackActiveReload(runCoalescedReload())
    })

    const consultationsQuery = query(
      collection(firestore, 'consultations'),
      where('customerId', '==', customerId),
      where('featureKey', '==', 'clinical_consultation'),
    )
    unsubscribeConsultationsQuery = onSnapshot(
      consultationsQuery,
      onConsultationsQuerySnapshot,
      (err) => {
        logger.error('[consultations-hub] consultations query listener failed', err)
        error.value = formatConsultationsHubFetchError(err)
        if (!hasLoaded.value) {
          isLoading.value = false
        } else {
          hasLoaded.value = false
        }
        teardownListeners()
      },
    )

    stopStripeCheckoutWatch = watch(
      () =>
        watchKeyForConsultationHubFirestoreWatches({
          summaries: summaries.value,
          activeClinicalConsultationId:
            customerStore.customer?.activeClinicalConsultationId ?? null,
        }),
      () => {
        attachStripeCheckoutListeners()
      },
      { immediate: true },
    )

    stopActiveIdWatch = watch(
      () => customerStore.customer?.activeClinicalConsultationId ?? null,
      (nextId, previousId) => {
        if (previousId === undefined || nextId === previousId) {
          return
        }
        signalReload({ immediate: false })
      },
    )
  }

  async function reloadList(): Promise<void> {
    const generation = ++reloadGeneration
    const customerId = customerStore.customer?.id ?? null
    const phase = hasLoaded.value ? 'refresh' : 'initial'
    const startedAt = performance.now()

    if (hasLoaded.value) {
      isRefreshing.value = true
    } else {
      isLoading.value = true
      error.value = null
    }

    try {
      for (let attempt = 1; attempt <= CONSULTATIONS_HUB_FETCH_MAX_ATTEMPTS; attempt += 1) {
        if (generation !== reloadGeneration) {
          return
        }

        try {
          const response = await platformCoordApi.listCustomerConsultationDetails({})
          if (generation !== reloadGeneration) {
            return
          }

          summaries.value = response.summaries
          followupBooking.value = response.followupBooking ?? DEFAULT_FOLLOWUP_BOOKING
          adminBookingInProgress.value = response.adminBookingInProgress ?? null
          hasLoaded.value = true
          error.value = null
          return
        } catch (err) {
          if (generation !== reloadGeneration) {
            logConsultationsHubFetchFailure({
              customerId,
              phase,
              attempt,
              maxAttempts: CONSULTATIONS_HUB_FETCH_MAX_ATTEMPTS,
              durationMs: Math.round(performance.now() - startedAt),
              error: err,
              superseded: true,
            })
            return
          }

          const durationMs = Math.round(performance.now() - startedAt)
          const shouldRetry =
            attempt < CONSULTATIONS_HUB_FETCH_MAX_ATTEMPTS &&
            isTransientConsultationsHubFetchError(err)

          logConsultationsHubFetchFailure({
            customerId,
            phase,
            attempt,
            maxAttempts: CONSULTATIONS_HUB_FETCH_MAX_ATTEMPTS,
            durationMs,
            error: err,
          })

          if (shouldRetry) {
            await delayMs(CONSULTATIONS_HUB_FETCH_RETRY_DELAYS_MS[attempt - 1] ?? 1500)
            continue
          }

          if (phase === 'initial') {
            error.value = formatConsultationsHubFetchError(err)
          }
          throw err
        }
      }
    } finally {
      if (generation === reloadGeneration) {
        isLoading.value = false
        isRefreshing.value = false
      }
    }
  }

  async function retryLoad(): Promise<void> {
    const customerId = customerStore.customer?.id
    if (!customerId) {
      return
    }
    if (!unsubscribeConsultationsQuery) {
      setupForCustomer(customerId)
    }
    while (activeReloadPromise) {
      const currentPromise = activeReloadPromise
      try {
        await currentPromise
        if (!activeReloadPromise || activeReloadPromise === currentPromise) {
          if (hasLoaded.value) {
            return
          }
          break
        }
      } catch {
        if (!activeReloadPromise || activeReloadPromise === currentPromise) {
          break
        }
      }
    }
    const promise = reloadList()
    activeReloadPromise = promise
    try {
      await promise
    } finally {
      if (activeReloadPromise === promise) {
        activeReloadPromise = null
      }
    }
  }

  async function requestUpcomingRefresh(): Promise<void> {
    const customerId = customerStore.customer?.id
    if (!customerId) {
      return
    }
    if (!unsubscribeConsultationsQuery) {
      setupForCustomer(customerId)
    }

    isUpcomingRefreshing.value = true
    debouncedReloadList?.cancel()

    const promise = runCoalescedReload()
    trackActiveReload(promise)
    try {
      await promise
    } catch (err) {
      logger.error('[consultations-hub] requestUpcomingRefresh failed', err)
    } finally {
      isUpcomingRefreshing.value = false
    }
  }

  function signOut(): void {
    teardownListeners()
    clearState()
  }

  function summaryById(consultationId: string): CustomerConsultationSummary | undefined {
    return summaries.value.find((row) => row.consultationId === consultationId)
  }

  watch(
    () => customerStore.customer?.id ?? null,
    (customerId) => {
      if (!customerId) {
        teardownListeners()
        clearState()
        return
      }
      setupForCustomer(customerId)
    },
    { immediate: true },
  )

  return {
    summaries,
    followupBooking,
    adminBookingInProgress,
    hasLoaded,
    isLoading,
    isRefreshing,
    isUpcomingRefreshing,
    error,
    featuredSummary,
    upcomingSummary,
    inProgressSummaries,
    pastSummaries,
    hasPastAppointments,
    clientVisibleSummaries,
    hasAnyConsultations,
    retryLoad,
    requestUpcomingRefresh,
    signOut,
    summaryById,
    hubNow,
  }
})
